import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { router } from "@inertiajs/react";
import useSubscriptionModal from "@hooks/use_subscription_modal.ts";
import BackButton from "@components/back_button.tsx";
import { EmailSchedulesContext } from "@components/preferences";
import type { EmailSchedule } from "@components/job_alerts/types.ts";
import { JobAlertsHeader, SubscriptionCountProvider } from "@components/job_alerts/header.tsx";
import { ErrorState } from "@components/job_alerts/error_state.tsx";
import { LoadingState } from "@components/job_alerts/loading_state.tsx";
import { JobAlertsGrid } from "@components/job_alerts/grid.tsx";
import { PausedBanner } from "@components/job_alerts/paused_banner.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";

export type JobAlertsPanelProps = {
  paths: {
    notificationSchedulePath: string;
    emailPreferencesPath: string;
  };
  emailSchedule: EmailSchedule;
  emailSchedules: EmailSchedule[];
  jobAlertsPaused: boolean;
};

type Props = JobAlertsPanelProps & {
  variant: "page" | "tab";
};

export const JobAlertsPanel = ({
  paths,
  emailSchedule,
  emailSchedules,
  jobAlertsPaused,
  variant,
}: Props) => {
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [pendingEditPath, setPendingEditPath] = useState<string | null>(null);
  const [pendingCompanyName, setPendingCompanyName] = useState<string>("");
  const { openModal } = useSubscriptionModal();

  const handleEditWhilePaused = (editPath: string, companyName: string) => {
    setPendingEditPath(editPath);
    setPendingCompanyName(companyName);
    setShowPausedModal(true);
  };

  const navigateToEdit = () => {
    setShowPausedModal(false);
    if (!pendingEditPath) return;

    if (variant === "tab") {
      openModal(pendingEditPath);
    } else {
      router.visit(pendingEditPath);
    }
  };

  return (
    <>
      <EmailSchedulesContext.Provider
        value={{
          emailSchedule,
          emailSchedules,
          notificationSchedulePath: paths.notificationSchedulePath,
        }}
      >
        <ErrorBoundary fallback={<ErrorState />}>
          <Suspense fallback={<LoadingState />}>
            <SubscriptionCountProvider>
              {variant === "page" && <BackButton />}
              <JobAlertsHeader jobAlertsPaused={jobAlertsPaused} variant={variant} />
              {jobAlertsPaused && (
                <PausedBanner emailPreferencesPath={paths.emailPreferencesPath} />
              )}
              <JobAlertsGrid
                onEditWhilePaused={jobAlertsPaused ? handleEditWhilePaused : undefined}
                modal={variant === "tab"}
              />
            </SubscriptionCountProvider>
          </Suspense>
        </ErrorBoundary>
      </EmailSchedulesContext.Provider>
      <EnableAlertsModal
        open={showPausedModal}
        onOpenChange={setShowPausedModal}
        companyName={pendingCompanyName}
        emailPreferencesPath={paths.emailPreferencesPath}
        onDismiss={navigateToEdit}
      />
    </>
  );
};
